import {
  Box,
  Stack,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Typography,
  TextField,
} from '@mui/material';
import { Provider, ResourceType, type LimitData } from '@repo/api-gw-sdk';
import { useCallback, useMemo, useState } from 'react';

import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useUser } from '@/contexts/useUser';
import { CloudProviders } from '@/data/inventory/data';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import theme from '@/theme';
import { fileSizeFormatter } from '@/utils/fileSizeFormatter';

import type { ConfigureProjectLimitsEditorState } from './configureProjectLimitsWizard';

import { CircleImage } from '../shared/circleImage';

const SupportedObjectCountLimitResourceTypes: string[] = [
  ResourceType.AwsS3,
  ResourceType.GcpCloudStorageBucket,
];

export const ConfigureProjectLimitsEditor = (
  props: StepProps<ConfigureProjectLimitsEditorState>
) => {
  const { isEmployee } = useUser();

  if (!isEmployee) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Typography variant='h2' color='error'>
          You are not authorized to configure project limits.
        </Typography>
      </Box>
    );
  }

  return (
    <StepContainer
      hideStepper
      onBackClick={props.back}
      canGoNext={() => true}
      nextButtonText={'Save Changes'}
      backButtonText='Cancel'
      onNextClick={() => props.finish()}
    >
      <LimitEditor
        limits={props.wizardState.limits}
        setLimits={(limits) =>
          props.setWizardState({ ...props.wizardState, limits })
        }
      />
    </StepContainer>
  );
};

const LimitEditor = ({
  limits,
  setLimits,
}: {
  limits: Record<string, LimitData>;
  setLimits: (limits: Record<string, LimitData>) => void;
}) => {
  const [originalLimits] = useState({ ...limits });

  return (
    <Stack>
      <Table>
        <TableHead>
          <TableRow
            sx={{
              borderTop: '1px solid var(--mui-palette-divider)',

              '& th': {
                textTransform: 'uppercase',
                borderRight: '1px solid var(--mui-palette-divider)',
                padding: 0,
                width: '20%',

                '& div': {
                  fontWeight: 'normal',
                  padding: '12px 16px',
                  backgroundColor: 'var(--mui-palette-background-tableHeader)',
                },

                '&:first-of-type div': {
                  marginLeft: 0,
                  paddingLeft: '40px',
                },
              },
            }}
          >
            <TableCell>
              <Box>
                <Typography variant='overline'>Resource Type</Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Box>
                <Typography variant='overline'>Max Resource Limit</Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Box>
                <Typography variant='overline'>Max Size Limit</Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Box>
                <Typography variant='overline'>
                  Max Object Count Limit
                </Typography>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        {originalLimits && (
          <TableBody>
            {Object.entries(ResourceTypes)
              .filter(
                ([, resourceTypeData]) =>
                  resourceTypeData.provider !== Provider.ProviderUnspecified
              )
              .sort(
                (a, b) =>
                  a[1].provider.localeCompare(b[1].provider) ||
                  a[1].displayName.localeCompare(b[1].displayName)
              )
              .map(([resourceType, resourceTypeData]) => {
                const limit = limits[resourceType];
                const provider = resourceTypeData.provider;
                return (
                  <TableRow
                    key={resourceType}
                    sx={{
                      '& td': {
                        borderRight: '1px solid var(--mui-palette-divider)',
                        padding: '12px 16px',

                        '&:first-of-type': {
                          paddingX: '36px',
                        },
                      },
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Stack direction='row' alignItems='center' gap='8px'>
                          <CircleImage
                            alt={CloudProviders[provider].displayName}
                            src={CloudProviders[provider].logo}
                          />
                          <Typography>
                            {ResourceTypes[resourceType].displayName}
                          </Typography>
                        </Stack>
                      </Box>
                    </TableCell>
                    <LimitCell
                      limit={limit?.quotaLimit}
                      onChange={(limit) => {
                        setLimits({
                          ...limits,
                          [resourceType]: {
                            ...limits[resourceType],
                            quotaLimit: limit,
                          },
                        });
                      }}
                      isEdited={
                        limits[resourceType]?.quotaLimit !==
                        originalLimits[resourceType]?.quotaLimit
                      }
                    />
                    <LimitCell
                      limit={limit?.sizeLimit}
                      onChange={(limit) => {
                        setLimits({
                          ...limits,
                          [resourceType]: {
                            ...limits[resourceType],
                            sizeLimit: limit,
                          },
                        });
                      }}
                      isEdited={
                        limits[resourceType]?.sizeLimit !==
                        originalLimits[resourceType]?.sizeLimit
                      }
                      convertToBytes
                    />
                    <LimitCell
                      limit={limit?.objectCountLimit}
                      onChange={(limit) => {
                        setLimits({
                          ...limits,
                          [resourceType]: {
                            ...limits[resourceType],
                            objectCountLimit: limit,
                          },
                        });
                      }}
                      isEdited={
                        limits[resourceType]?.objectCountLimit !==
                        originalLimits[resourceType]?.objectCountLimit
                      }
                      disabled={
                        !SupportedObjectCountLimitResourceTypes.includes(
                          resourceType
                        )
                      }
                    />
                  </TableRow>
                );
              })}
          </TableBody>
        )}
      </Table>
    </Stack>
  );
};

const LimitCell = ({
  limit,
  onChange,
  convertToBytes = false,
  isEdited = false,
  disabled = false,
}: {
  limit: number | null | undefined;
  onChange: (limit: number | undefined) => void;
  convertToBytes?: boolean;
  isEdited?: boolean;
  disabled?: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const text = useMemo(() => {
    if (convertToBytes && !isEditing && limit !== null && limit !== undefined) {
      return fileSizeFormatter(limit);
    }
    return limit;
  }, [limit, convertToBytes, isEditing]);

  const focusedRef = useCallback((node: HTMLInputElement | null) => {
    if (node) {
      node.focus();
      node.select();
    }
  }, []);

  return (
    <TableCell
      sx={
        isEdited
          ? { backgroundColor: 'var(--mui-palette-warning-bgLightHover)' }
          : disabled
            ? { backgroundColor: 'var(--mui-palette-background-disabled)' }
            : {}
      }
      onClick={() => !disabled && setIsEditing(true)}
    >
      <Box>
        {isEditing ? (
          <TextField
            sx={{
              width: '100%',
            }}
            inputProps={{
              style: {
                fontSize: theme().typography.body1.fontSize,
                padding: theme().typography.body1.padding || 0,
              },
            }}
            value={text ?? ''}
            type='number'
            variant='standard'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value !== '' ? +e.target.value : undefined);
            }}
            onBlur={() => {
              setIsEditing(false);
            }}
            inputRef={focusedRef}
          />
        ) : (
          <Typography variant='body1'>{text}</Typography>
        )}
      </Box>
    </TableCell>
  );
};
